import { Plus, Share, X } from 'lucide-react'
import { useSyncExternalStore } from 'react'
import { Button } from '#app/components/ui/button.tsx'

const DISMISS_KEY = 'plq:install-tip-dismissed'

// ── Dismissal state ──────────────────────────────────────────────────────
const dismissListeners = new Set<() => void>()

function notifyDismissChange() {
	for (const listener of dismissListeners) listener()
}

function subscribeDismissed(callback: () => void) {
	dismissListeners.add(callback)
	return () => {
		dismissListeners.delete(callback)
	}
}

function getDismissedSnapshot() {
	try {
		return window.localStorage.getItem(DISMISS_KEY) === '1'
	} catch {
		return false
	}
}

function getDismissedServerSnapshot() {
	return true
}

function dismissTip() {
	try {
		window.localStorage.setItem(DISMISS_KEY, '1')
	} catch {
		// Private mode — in-memory notify still hides for this session
	}
	notifyDismissChange()
}

// ── iOS Safari detection ─────────────────────────────────────────────────
function getIosEligibleSnapshot() {
	const nav = window.navigator
	const ua = nav.userAgent
	const isIphone = /iPhone|iPod/.test(ua)
	const isModernIpad = ua.includes('Macintosh') && nav.maxTouchPoints > 1
	if (!isIphone && !isModernIpad) return false
	const standaloneNav = (nav as { standalone?: boolean }).standalone === true
	const standaloneMedia = window.matchMedia(
		'(display-mode: standalone)',
	).matches
	return !standaloneNav && !standaloneMedia
}

function getFalseSnapshot() {
	return false
}

function subscribeNoop() {
	return () => {}
}

// ── Android beforeinstallprompt ──────────────────────────────────────────
type BeforeInstallPromptEvent = Event & {
	prompt: () => Promise<void>
	userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let cachedPromptEvent: BeforeInstallPromptEvent | null = null
const promptListeners = new Set<() => void>()

function notifyPromptChange() {
	for (const listener of promptListeners) listener()
}

// Listener attached at module load — the browser fires beforeinstallprompt
// once, very early, often before any component has mounted.
if (typeof window !== 'undefined') {
	window.addEventListener('beforeinstallprompt', (event) => {
		event.preventDefault()
		cachedPromptEvent = event as BeforeInstallPromptEvent
		notifyPromptChange()
	})
	window.addEventListener('appinstalled', () => {
		cachedPromptEvent = null
		notifyPromptChange()
	})
}

function subscribePrompt(callback: () => void) {
	promptListeners.add(callback)
	return () => {
		promptListeners.delete(callback)
	}
}

function getPromptSnapshot() {
	return cachedPromptEvent
}

function getPromptServerSnapshot(): BeforeInstallPromptEvent | null {
	return null
}

// ── Component ────────────────────────────────────────────────────────────
export function IosInstallTip() {
	const iosEligible = useSyncExternalStore(
		subscribeNoop,
		getIosEligibleSnapshot,
		getFalseSnapshot,
	)
	const androidPrompt = useSyncExternalStore(
		subscribePrompt,
		getPromptSnapshot,
		getPromptServerSnapshot,
	)
	const dismissed = useSyncExternalStore(
		subscribeDismissed,
		getDismissedSnapshot,
		getDismissedServerSnapshot,
	)

	if (dismissed) return null

	if (androidPrompt) return <AndroidInstallCard event={androidPrompt} />
	if (iosEligible) return <IosInstallCard />
	return null
}

function AndroidInstallCard({ event }: { event: BeforeInstallPromptEvent }) {
	const onInstall = async () => {
		await event.prompt()
		const choice = await event.userChoice
		if (choice.outcome === 'dismissed') dismissTip()
		// On accept, the browser fires `appinstalled` which clears the cached
		// event → component unmounts on its own.
	}

	return (
		<aside
			aria-label="Installer l'app"
			className="border-border/60 bg-muted/40 mb-4 flex items-center gap-3 rounded-xl border p-3"
		>
			<span
				aria-hidden="true"
				className="bg-background flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
			>
				<Plus className="h-4 w-4" />
			</span>
			<div className="flex-1 text-sm leading-snug">
				<p className="font-semibold">Installer l'app PLQ</p>
				<p className="text-muted-foreground mt-0.5">
					Accès rapide depuis ton écran d'accueil, plein écran.
				</p>
			</div>
			<Button type="button" size="sm" onClick={onInstall} className="shrink-0">
				Installer
			</Button>
			<button
				type="button"
				onClick={dismissTip}
				aria-label="Masquer l'astuce"
				className="text-muted-foreground hover:text-foreground flex h-touch w-touch shrink-0 items-center justify-center"
			>
				<X className="h-4 w-4" aria-hidden="true" />
			</button>
		</aside>
	)
}

function IosInstallCard() {
	return (
		<aside
			aria-label="Astuce d'installation"
			className="border-border/60 bg-muted/40 mb-4 rounded-xl border p-3"
		>
			<div className="flex items-start gap-3">
				<span
					aria-hidden="true"
					className="bg-background flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
				>
					<Share className="h-4 w-4" />
				</span>
				<div className="flex-1">
					<div className="flex items-start justify-between gap-2">
						<p className="text-sm font-semibold">
							Installe l'app PLQ sur ton iPhone
						</p>
						<button
							type="button"
							onClick={dismissTip}
							aria-label="Masquer l'astuce"
							className="text-muted-foreground hover:text-foreground -mt-1 -mr-1 flex h-touch w-touch shrink-0 items-center justify-center"
						>
							<X className="h-4 w-4" aria-hidden="true" />
						</button>
					</div>
					<ol className="text-muted-foreground mt-1.5 space-y-1.5 text-sm">
						<li className="flex items-start gap-2">
							<span
								aria-hidden="true"
								className="bg-background text-foreground mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-[10px] font-bold"
							>
								1
							</span>
							<span>
								Ouvre le menu de Safari et touche{' '}
								<Share
									aria-hidden="true"
									className="inline h-3.5 w-3.5 align-text-bottom"
								/>{' '}
								<strong className="text-foreground">Partager</strong>
							</span>
						</li>
						<li className="flex items-start gap-2">
							<span
								aria-hidden="true"
								className="bg-background text-foreground mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-[10px] font-bold"
							>
								2
							</span>
							<span>
								Fais défiler puis touche{' '}
								<Plus
									aria-hidden="true"
									className="inline h-3.5 w-3.5 align-text-bottom"
								/>{' '}
								<strong className="text-foreground">
									Ajouter sur l'écran d'accueil
								</strong>
							</span>
						</li>
					</ol>
				</div>
			</div>
		</aside>
	)
}
